import { call, put } from "redux-saga/effects";
import { push } from "connected-react-router";
import { toast } from "react-toastify";
import { logger } from "../../utils/logger";
import { actions } from "../../actions/auth";
import { ROUTES } from "../../routes/constants";
import axios from "axios";

import {
  getNpStaffUsersAPI,
  initiatePasswordResetAPI,
  loginAPI,
  logoutAPI,
  passwordResetAPI,
  requestOtpAPI,
  verifyOtpAPI
} from "../../api/auth";
import {
  setLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageObject,
  removeSessionStorageItem
} from "../../utils/helper";
import {
  IS_ORDERS_FILTER_OPEN,
  IS_SHOW_ORDERS,
  IS_SHOW_RIDERS,
  SELECTED_REGION_DETAIL,
  TOKEN,
  USER,
  VERTICALS
} from "../../utils/constants";
const normalizeUser = (rawUser = {}) => {
  if (!rawUser || typeof rawUser !== "object") return {};

  const source = { ...rawUser };
  const user = {
    id: source.id ?? null,
    firstName: source.first_name ?? source.firstName ?? "",
    lastName: source.last_name ?? source.lastName ?? "",
    userType: source.user_type ?? source.userType ?? null,
    phone: source.phone ?? null,
    email: source.email ?? null,
    cnic: source.cnic ?? null,
    address: source.address ?? null,
    dateJoined: source.date_joined ?? source.dateJoined ?? null,
    dob: source.dob ?? null,
    gender: source.gender ?? null,
    hasFilledHearAboutUs:
      source.has_filled_hear_about_us ?? source.hasFilledHearAboutUs ?? false,
    userPermissions:
      source.user_permissions ?? source.userPermissions ?? [],
    raw: source,
  };

  return user;
};

export function* attemptLogin({ payload }) {
  try {
    console.log("[LOGIN] attempt payload:", payload);
    const resp = yield call(loginAPI, payload);

    console.log("[LOGIN] raw axios response:", resp);
    const data = resp?.data ?? resp;
    console.log("[LOGIN] parsed data:", data);
    const twoFaRequired =
      data?.["2fa_required"] === true ||
      data?.["2FaRequired"] === true ||
      data?.["requires_2fa"] === true;

    const pendingToken = data?.pending_token || data?.pendingToken || null;
    const methods = Array.isArray(data?.methods) ? data.methods : [];

    console.log("[LOGIN] branch check -> twoFaRequired:", twoFaRequired, "pendingToken:", pendingToken, "methods:", methods);

    if (twoFaRequired) {
      if (!pendingToken) {
        console.warn("[LOGIN] 2FA required but no pendingToken! Failing closed.");
        yield put(actions.loginFailure(new Error("2FA misconfigured: no pendingToken")));
        toast.error("Authentication services are misconfigured. Please contact support.");
        return;
      }

      const context = {
        is_dashboard: Boolean(payload?.is_dashboard),
        push_key: payload?.push_key ?? null,
        onesignal_player_id: payload?.onesignal_player_id ?? null,
        app_version: payload?.app_version ?? null,
      };

      const normalizedUser = normalizeUser(data.user);

      console.log("[LOGIN] normalized permissions during 2FA:", normalizedUser.userPermissions);

      const twoFAState = {
        pendingToken,
        methods: methods.length ? methods : ["totp"],
        user: normalizedUser,
        context,
      };
      try {
        sessionStorage.setItem("twoFA", JSON.stringify(twoFAState));
        console.log("[LOGIN] stored twoFA in sessionStorage:", twoFAState);
      } catch (e) {
        console.warn("[LOGIN] failed to save twoFA to sessionStorage:", e);
      }

      console.log("[LOGIN] dispatch twoFARequired + navigate to /otp");
      yield put(actions.twoFARequired(twoFAState));
      yield put(push(ROUTES.OTP_VERIFY));
      return;
    }

    if (!data?.token) {
      console.warn("[LOGIN] no token returned and 2FA not required -> treating as error");
      yield put(actions.loginFailure(new Error("No token returned")));
      toast.error(data?.detail || "Login failed. Please try again.");
      return;
    }
    const normalizedUser = normalizeUser(data.user);
    console.log("[LOGIN] normalized permissions:", normalizedUser.userPermissions);
    const loginPayload = { ...data, user: normalizedUser };

    console.log("[LOGIN] normal login, token present -> saving + redirect");
    yield put(actions.loginSuccess(loginPayload));
    setLocalStorageItem(TOKEN, data.token);
    setLocalStorageObject(USER, normalizedUser);
    yield put(push(ROUTES.BLOOD_DASHBOARD));
  } catch (error) {
    console.error("[LOGIN] error:", error);
    logger.error(error);

    if (error?.response?.status === 503) {
      toast.error("Authentication services are misconfigured. Please contact support.");
    } else if (error?.response?.status === 401) {
      toast.error("Invalid phone number or password");
    } else if (error?.response?.data?.detail) {
      toast.error(error.response.data.detail);
    } else {
      toast.error("Login failed. Please try again.");
    }

    yield put(actions.loginFailure(error));
  }
}



export function* attemptRequestOtp({ payload }) {
  try {
    const resp = yield call(requestOtpAPI, payload);
    if (resp) {
      yield put(actions.requestOtpSuccess());
      toast.success("OTP sent");
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.requestOtpFailure(error));
    toast.error(error?.response?.data?.detail || "Failed to send OTP");
  }
}

export function* attemptVerifyOtp({ payload }) {
  try {
    const resp = yield call(verifyOtpAPI, payload);
    if (resp) {
      const data = resp.data;
      const normalizedUser = normalizeUser(data.user);
      console.log("[VERIFY OTP] normalized permissions:", normalizedUser.userPermissions);
      const payload = { ...data, user: normalizedUser };

      yield put(actions.verifyOtpSuccess(payload));
      setLocalStorageItem(TOKEN, data.token);
      setLocalStorageObject(USER, normalizedUser);
      yield put(actions.loginSuccess(payload));
      yield put(push(ROUTES.BLOOD_DASHBOARD));
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.verifyOtpFailure(error));
    toast.error(error?.response?.data?.detail || "Invalid or expired code");
  }
}

export function* attemptLogout({ payload }) {
  try {
    const resp = yield call(logoutAPI, payload);
    if (resp) {
      yield put(actions.logoutSuccess());
      removeLocalStorageItem(USER);
      removeLocalStorageItem(TOKEN);
      removeLocalStorageItem(IS_SHOW_ORDERS);
      removeLocalStorageItem(IS_SHOW_RIDERS);
      removeLocalStorageItem(SELECTED_REGION_DETAIL);
      removeLocalStorageItem(IS_ORDERS_FILTER_OPEN);
      removeSessionStorageItem(VERTICALS.XRAY);
      removeSessionStorageItem(VERTICALS.BLOOD);
      yield put(push("/"));
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.logoutFailure(error));
  }
}

export function* getNpStaffUsers({ payload: values }) {
  try {
    const resp = yield call(getNpStaffUsersAPI, values);
    if (resp) {
      yield put(actions.getNpStaffUsersSuccess(resp.data));
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.getNpStaffUsersFailure(error));
  }
}

export function* initiatePasswordReset({ payload: value }) {
  try {
    const resp = yield call(initiatePasswordResetAPI, value);
    if (resp) {
      yield put(actions.initiatePasswordResetSuccess(resp.data));
      toast.success("Password reset has been requested!");
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.initiatePasswordResetFailure(error));
  }
}

export function* passwordReset({ payload: value }) {
  try {
    const resp = yield call(passwordResetAPI, value);
    if (resp) {
      yield put(actions.passwordResetSuccess(resp.data));
      toast.success("Password changed successfully!");
    }
  } catch (error) {
    logger.error(error);
    yield put(actions.passwordResetFailure(error));
  }
}



